/**
 * DataEntryAgent — «הזנה חכמה» (ADR-0037, Turbo's DataEntryAgent): an AI agent that turns a whole
 * Excel / bank file into verified records without typing them one by one and without a blind import.
 *
 * Flow: upload → what is in the file + column roles + defaults (ledger, category, payment method) →
 * the agent scans → a deck of rows (Enter = approve & next, Shift+Enter = back) or a table for bulk
 * approval → each approved row is written in the background (queue: 25 rows every 700ms) through the
 * regular services, undo per row → summary. The rows and their edits live here, on the client; the
 * session itself (file, proposals, what was written) is kept on the server.
 * Phases 3–4: a file that is a list of customers / suppliers goes to DataEntryEntity; a bank statement
 * can be loaded into the ledger first (DataEntryBankLoad); a file can be saved as a recurring upload.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Dialog,
  EntityCard,
  Field,
  ProgressBar,
  Segmented,
  TextInput,
  ckConfirm,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataEntryApi,
  type AppliedRuleDto,
  type ColumnGuessDto,
  type CounterpartyDto,
  type CounterpartyType,
  type DataEntrySessionDto,
  type EntityTarget,
  type ImportType,
  type RecurringDto,
  type RowKind,
  type RowPatchDto,
  type RuleThen,
  type RuleWhen,
  type SavedRuleDto,
  type SimilarGroupDto,
} from "../../../api/aiDataEntryApi";
import { notifyDataChanged } from "../../events";
import { AgentChat, type AgentChatAction, type AgentChatMessage } from "../shared/AgentChat";
import DataEntryBankLoad, { type BankLoadChoice } from "./DataEntryBankLoad";
import DataEntryCard, { type FieldRefs, type LearnState } from "./DataEntryCard";
import DataEntryEntity from "./DataEntryEntity";
import DataEntryInsights from "./DataEntryInsights";
import DataEntryTable from "./DataEntryTable";
import RecurringUploadDialog, { RecurringSection } from "./RecurringUploadDialog";
import {
  ChatTips,
  ColumnsTab,
  DefaultsSection,
  EmptyFilter,
  FileSection,
  HowItWorks,
  NearbySection,
  ProgressSection,
  RowFileSection,
  RowUnderstandingSection,
  SavedRulesSection,
  SummaryTab,
  TypeTab,
  UploadTab,
  type Counts,
} from "./DataEntryWorkspace";
import {
  commitPayload,
  isEntityImport,
  isFinished,
  isTypingTarget,
  KIND_META,
  KIND_ORDER,
  learnableChange,
  matchesFlag,
  money,
  normName,
  rowProblems,
  rowsStateOf,
  rowState,
  toLocalRow,
  wantedCounterpartyType,
  type LocalRow,
} from "./dataEntryModel";
import "./dataEntry.css";

const SCAN_PHASES = [
  "קורא את כל השורות…",
  "מזהה לכל שורה אם היא תשלום מלקוח, הכנסה, הוצאה או מכירה…",
  "מחפש את הלקוח / הספק במערכת…",
  "בודק כפילויות ורשומות שכבר קיימות…",
  "מזהה תשלומים מחזוריים ומנסח תיאורים…",
];
const FLUSH_MS = 700;
const BATCH = 25;
const FILE_STARTERS = ["מה בקובץ הזה, ומה דורש את תשומת הלב שלי?", "למה השורות שבבדיקה לא מוכנות?", "כמה כסף יש לפי אמצעי תשלום?", "כל השורות של ביט הן תשלומים מלקוחות"];

type Step = "upload" | "setup" | "scan" | "work" | "done" | "entity";
type Creating = { type: CounterpartyType; index: number; name: string; phone: string; email: string; idNumber: string; busy: boolean };

let chatSeq = 0;
const chatId = () => {
  chatSeq += 1;
  return `c${chatSeq}`;
};
const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

function ScanStep() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setPhase((p) => Math.min(p + 1, SCAN_PHASES.length - 1)), 1800);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="de-scan">
      <span className="de-scan__ic" aria-hidden="true">
        ✦
      </span>
      <b>הסוכן עובר על הקובץ</b>
      <span className="de-muted">{SCAN_PHASES[phase]}</span>
      <ProgressBar label="סריקה" />
    </div>
  );
}

function csvCell(v: unknown) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export type DataEntryAgentProps = {
  open: boolean;
  onClose?: () => void;
  initialType?: ImportType | null;
  sessionId?: string | null;
};

export default function DataEntryAgent({ open, onClose, initialType = null, sessionId = null }: DataEntryAgentProps) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("upload");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<DataEntrySessionDto | null>(null);
  const [columns, setColumns] = useState<ColumnGuessDto[]>([]);
  const [importType, setImportType] = useState<ImportType>("auto");
  const [fileRules, setFileRules] = useState<Array<{ when: RuleWhen; then: RuleThen }>>([]);
  const [savedRules, setSavedRules] = useState<SavedRuleDto[]>([]);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [rows, setRows] = useState<LocalRow[]>([]);
  const [cur, setCur] = useState(0);
  const [tab, setTab] = useState("upload");
  const [filter, setFilter] = useState("todo");
  const [flash, setFlash] = useState(false);
  const [learn, setLearn] = useState<LearnState | null>(null);
  const [creating, setCreating] = useState<Creating | null>(null);
  const [chat, setChat] = useState<AgentChatMessage[]>([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [entityTarget, setEntityTarget] = useState<EntityTarget | null>(null);
  const [bankChoice, setBankChoice] = useState<BankLoadChoice>("idle");
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [recurring, setRecurring] = useState<RecurringDto[]>([]);
  const rowsRef = useRef<LocalRow[]>(rows);
  rowsRef.current = rows;
  const sessionRef = useRef<DataEntrySessionDto | null>(session);
  sessionRef.current = session;
  const queue = useRef<Set<number>>(new Set());
  const flushTimer = useRef<number | undefined>(undefined);
  const flushing = useRef(false);
  const committedAny = useRef(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const paymentRef = useRef<HTMLSpanElement>(null);
  const fieldRefs: FieldRefs = useMemo(() => ({ amount: amountRef, date: dateRef, payment: paymentRef }), []);

  const sid = session?.id ?? null;
  const hasLedger = Boolean(session?.ledger);
  const lookups = session?.lookups ?? { categories: [], funds: [], reps: [], products: [], kinds: KIND_ORDER, paymentMethods: [] };

  const reset = () => {
    setStep("upload");
    setBusy(false);
    setError(null);
    setSession(null);
    setColumns([]);
    setImportType("auto");
    setFileRules([]);
    setRows([]);
    setCur(0);
    setTab("upload");
    setFilter("todo");
    setLearn(null);
    setCreating(null);
    setChat([]);
    setChatDraft("");
    setChatBusy(false);
    setActionNote(null);
    setEntityTarget(null);
    setBankChoice("idle");
    setRecurringOpen(false);
    queue.current = new Set();
    window.clearTimeout(flushTimer.current);
  };

  const statusOf = useCallback((r: LocalRow, ledger = Boolean(sessionRef.current?.ledger)): LocalRow["status"] => (rowProblems(r, { hasLedger: ledger }).length ? "review" : "ready"), []);

  /* ── the analysis arrives ── */
  const applyAnalysis = useCallback(
    (data: DataEntrySessionDto, { keepView = false }: { keepView?: boolean } = {}) => {
      setSession(data);
      setColumns(data.columns);
      setImportType(data.importType);
      setSavedRules(data.savedRules);
      const local = data.rows.map(toLocalRow);
      // rows already written in this session stay written (re-analysis / a resumed file)
      data.committed.forEach((c) => {
        if (local[c.index]) local[c.index] = { ...local[c.index]!, approved: true, commit: { state: "done", result: c } };
      });
      setRows(local);
      if (!keepView) {
        const firstReview = local.findIndex((r) => !isFinished(r) && rowState(r) === "review");
        const firstTodo = local.findIndex((r) => !isFinished(r) && r.kind !== "skip");
        setCur(Math.max(0, firstReview >= 0 ? firstReview : firstTodo));
        setTab(local.length > 400 ? "table" : "rows");
      } else {
        setCur((c) => Math.min(c, Math.max(0, local.length - 1)));
      }
      setChat(
        data.chat.map((m) => ({
          id: chatId(),
          role: m.role === "assistant" ? "agent" : "user",
          text: m.text,
          steps: m.steps,
        })),
      );
      setStep("work");
    },
    [],
  );

  const toSetup = (data: DataEntrySessionDto) => {
    setSession(data);
    setColumns(data.columns);
    setSavedRules(data.savedRules);
    const allowed = (t: ImportType) => !isEntityImport(t) || (data.lookups.entityTargets ?? []).includes(t);
    // a recurring upload brings its own type; otherwise the file's shape, then what the opener asked for
    const wanted = data.recurring?.applied ? data.importType : data.suggestedType !== "auto" ? data.suggestedType : (initialType ?? data.importType);
    setImportType(allowed(wanted) ? wanted : "auto");
    setStep("setup");
    setTab("type");
  };

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      toSetup(await aiDataEntryApi.upload(file));
    } catch (e) {
      setError(errorText(e, "קריאת הקובץ נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  // opened on a file from the list (?session= / the recent files)
  const resumed = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !sessionId || resumed.current === sessionId) return;
    resumed.current = sessionId;
    setBusy(true);
    aiDataEntryApi
      .get(sessionId)
      .then((s) => {
        if (s.analyzed) applyAnalysis(s);
        else {
          toSetup(s);
          // a list of customers / suppliers that was already scanned: back to its rows
          if (s.entityTarget && isEntityImport(s.importType)) {
            setImportType(s.importType);
            setEntityTarget(s.entityTarget);
            setStep("entity");
          }
        }
      })
      .catch((e) => setError(errorText(e, "הקובץ לא נמצא")))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  // the saved rules and the recurring uploads are shown before any file is chosen
  useEffect(() => {
    if (!open) return;
    aiDataEntryApi
      .listRules()
      .then(setSavedRules)
      .catch(() => undefined);
    aiDataEntryApi
      .listRecurring()
      .then(setRecurring)
      .catch(() => undefined);
  }, [open]);

  const analyzeBody = (cols?: ColumnGuessDto[], rules?: Array<{ when: RuleWhen; then: RuleThen }>) => ({
    importType,
    columns: (cols ?? columns).map((c) => ({ header: c.header, role: c.role })),
    rules: rules ?? fileRules,
  });

  const runAnalyze = async ({ keepView = false, rules, cols, ledgerId }: { keepView?: boolean; rules?: Array<{ when: RuleWhen; then: RuleThen }>; cols?: ColumnGuessDto[]; ledgerId?: string | null } = {}) => {
    if (!sid) return null;
    setError(null);
    if (!keepView) {
      setStep("scan");
      setTab("scan");
    }
    try {
      const data = await aiDataEntryApi.analyze(sid, { ...analyzeBody(cols, rules), ...(ledgerId !== undefined ? { ledgerId } : {}) });
      applyAnalysis(data, { keepView });
      return data;
    } catch (e) {
      setError(errorText(e, "הסריקה נכשלה"));
      if (keepView) toast.error(errorText(e, "הסריקה נכשלה"));
      else {
        setStep("setup");
        setTab("type");
      }
      return null;
    }
  };

  /* ── commit queue ── */
  const patchRow = useCallback((index: number, patch: Partial<LocalRow> | ((r: LocalRow) => LocalRow)) => {
    setRows((rs) => {
      const prev = rs[index];
      if (!prev) return rs;
      const next = rs.slice();
      next[index] = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      return next;
    });
  }, []);

  const flush = useCallback(async () => {
    const s = sessionRef.current;
    if (flushing.current || !s) return;
    const batch = Array.from(queue.current).slice(0, BATCH);
    if (!batch.length) return;
    flushing.current = true;
    batch.forEach((i) => queue.current.delete(i));
    setRows((rs) => {
      const next = rs.slice();
      batch.forEach((i) => {
        if (next[i]) next[i] = { ...next[i]!, commit: { state: "saving" } };
      });
      return next;
    });
    try {
      const payload = batch.map((i) => rowsRef.current[i]).filter((r): r is LocalRow => Boolean(r)).map(commitPayload);
      const { results } = await aiDataEntryApi.commit(s.id, payload);
      setRows((rs) => {
        const next = rs.slice();
        results.forEach((r) => {
          const row = next[r.index];
          if (!row) return;
          next[r.index] = r.ok ? { ...row, commit: { state: "done", result: r } } : { ...row, approved: false, commit: { state: "error", error: r.error ?? "הרישום נכשל" } };
        });
        return next;
      });
      if (results.some((r) => r.ok && r.id)) committedAny.current = true;
      const failed = results.filter((r) => !r.ok).length;
      if (failed) toast.error(`${failed} שורות לא נרשמו — מסומנות באדום`);
      const bankMissed = results.filter((r) => r.ok && r.bank && !r.bank.matched);
      if (bankMissed.length) toast(`${bankMissed.length} רשומות נרשמו בלי התאמה לשורת הבנק: ${bankMissed[0]!.bank!.error ?? ""}`);
    } catch (e) {
      const msg = errorText(e, "הרישום נכשל");
      setRows((rs) => {
        const next = rs.slice();
        batch.forEach((i) => {
          if (next[i]) next[i] = { ...next[i]!, approved: false, commit: { state: "error", error: msg } };
        });
        return next;
      });
      toast.error(msg);
    } finally {
      flushing.current = false;
      if (queue.current.size) flushTimer.current = window.setTimeout(() => void flush(), 50);
    }
  }, [toast]);

  const schedule = useCallback(() => {
    window.clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(() => void flush(), FLUSH_MS);
  }, [flush]);

  /** flush everything that is queued (closing, «סיום», a new scan) */
  const flushAll = useCallback(async () => {
    window.clearTimeout(flushTimer.current);
    for (let guard = 0; guard < 400 && (queue.current.size || flushing.current); guard++) {
      if (flushing.current) await new Promise((r) => window.setTimeout(r, 60));
      else await flush();
    }
  }, [flush]);

  /* ── navigation ── */
  const matchesFilter = useCallback(
    (r: LocalRow | undefined, f: string = filter) => {
      if (!r) return false;
      if (f === "all") return true;
      if (f === "todo") return !isFinished(r);
      if (f === "review") return ["review", "error"].includes(rowState(r));
      if (f === "done") return isFinished(r);
      if (f.startsWith("flag:")) return matchesFlag(r, f) && (r.index === cur || !isFinished(r));
      // rows the agent pointed at in the conversation
      if (f.startsWith("idx:")) return f.slice(4).split(",").includes(String(r.index));
      return true;
    },
    [filter, cur],
  );

  const visible = useMemo(() => rows.filter((r) => matchesFilter(r)).map((r) => r.index), [rows, matchesFilter]);

  const goNext = useCallback(
    (from: number, { skipFinished = true }: { skipFinished?: boolean } = {}) => {
      const rs = rowsRef.current;
      const ok = (r: LocalRow | undefined) => Boolean(r) && (skipFinished ? !isFinished(r) && r!.index !== from && matchesFilter(r) : true);
      for (let i = from + 1; i < rs.length; i += 1) {
        if (ok(rs[i])) {
          setCur(i);
          return true;
        }
      }
      for (let i = 0; i < from; i += 1) {
        if (ok(rs[i])) {
          setCur(i);
          return true;
        }
      }
      return false;
    },
    [matchesFilter],
  );

  const goBack = useCallback((from: number) => {
    if (from > 0) setCur(from - 1);
  }, []);

  /* ── edits & suggestions ── */
  const requestSuggest = useCallback(
    async (index: number, kind: RowKind, counterparty?: CounterpartyDto | null) => {
      const s = sessionRef.current;
      if (!s || kind === "skip") return;
      try {
        const sug = await aiDataEntryApi.suggest(s.id, { index, kind, counterparty: counterparty === undefined ? undefined : counterparty ? { type: counterparty.type, id: counterparty.id } : null });
        patchRow(index, (r) => {
          if (r.kind !== kind) return r; // changed again meanwhile
          const touched = r.touched;
          const next: LocalRow = { ...r, lastSeen: sug.lastSeen, candidates: sug.candidates, deals: sug.deals };
          if (counterparty === undefined) next.counterparty = sug.counterparty;
          if (!next.counterparty) next.newEntity = sug.newEntity;
          if (!touched.category) next.category = sug.category;
          if (!touched.fund) next.fund = sug.fund;
          if (!touched.payment && sug.paymentMethod) {
            next.paymentMethod = sug.paymentMethod;
            next.paymentSource = sug.paymentSource;
          }
          if (!touched.description) next.description = sug.description;
          if (!touched.deal) next.useDeal = sug.useDeal;
          if (!touched.product) next.product = sug.product;
          if (!touched.rep) next.rep = sug.rep;
          next.status = statusOf(next);
          return next;
        });
      } catch {
        /* suggestion is best effort */
      }
    },
    [patchRow, statusOf],
  );

  const onChange = useCallback(
    (index: number, patch: Partial<LocalRow>, field: string) => {
      const before = rowsRef.current[index];
      if (!before) return;
      patchRow(index, (r) => {
        const next: LocalRow = { ...r, ...patch, touched: { ...r.touched, [field]: true } };
        if (patch.kind && patch.kind !== r.kind) {
          next.kindReason = "נבחר ידנית";
          next.kindConfidence = 1;
          const want = wantedCounterpartyType(patch.kind);
          if (r.counterparty && r.counterparty.type !== want) next.counterparty = null;
          if (patch.kind !== "payment") next.useDeal = false;
        }
        if (r.commit.state === "error") next.commit = { state: "idle" };
        next.status = statusOf(next);
        return next;
      });
      if (patch.kind && patch.kind !== before.kind && patch.kind !== "skip") {
        const want = wantedCounterpartyType(patch.kind);
        void requestSuggest(index, patch.kind, before.counterparty?.type === want ? before.counterparty : undefined);
      }
      // the agent learns: the same change for similar rows?
      const change = learnableChange(field, patch);
      const s = sessionRef.current;
      if (change && s && !before.existing) {
        aiDataEntryApi
          .similar(s.id, { index, change, excludeIndexes: rowsRef.current.filter((r) => isFinished(r)).map((r) => r.index) })
          .then((res) => {
            const rs = rowsRef.current;
            const groups = res.groups
              .map((g) => ({ ...g, indexes: g.indexes.filter((i) => rs[i] && !isFinished(rs[i])) }))
              .map((g) => ({ ...g, count: g.indexes.length }))
              .filter((g) => g.count > 0);
            setLearn(groups.length ? { index, change, groups, busy: false } : null);
          })
          .catch(() => undefined);
      }
    },
    [patchRow, requestSuggest, statusOf],
  );

  const applyPatches = useCallback(
    (patches: Array<{ index: number; patch: RowPatchDto }>, touchedFrom?: "learn") => {
      patches.forEach(({ index, patch }) =>
        patchRow(index, (r) => {
          if (isFinished(r)) return r;
          const touched = touchedFrom ? { ...r.touched, ...(patch.kind ? { kind: true } : {}), ...(patch.category ? { category: true } : {}), ...(patch.fund ? { fund: true } : {}) } : r.touched;
          const next: LocalRow = { ...r, ...(patch as Partial<LocalRow>), touched, commit: r.commit.state === "error" ? { state: "idle" } : r.commit };
          next.status = statusOf(next);
          return next;
        }),
      );
    },
    [patchRow, statusOf],
  );

  const applyLearn = useCallback(
    async (group: SimilarGroupDto, saveRule: boolean) => {
      const s = sessionRef.current;
      if (!learn || !s) return;
      setLearn((l) => (l ? { ...l, busy: true } : l));
      try {
        const finished = rowsRef.current.filter((r) => isFinished(r)).map((r) => r.index);
        const res = await aiDataEntryApi.propagate(s.id, { index: learn.index, change: learn.change, groupKey: group.key, saveRule, excludeIndexes: finished });
        applyPatches(res.patches, "learn");
        toast.success(`הוחל על ${res.patches.length} שורות${res.rule ? " — ונשמר ככלל" : ""}`);
        if (res.rule) setSavedRules((rs) => [res.rule!, ...rs]);
        setLearn(null);
      } catch (e) {
        toast.error(errorText(e, "ההחלה נכשלה"));
        setLearn((l) => (l ? { ...l, busy: false } : l));
      }
    },
    [learn, applyPatches, toast],
  );

  /* ── rules ── */
  const applyRule = async (rule: { when: RuleWhen; then: RuleThen }, { save }: { save: boolean }) => {
    try {
      if (save) {
        const saved = await aiDataEntryApi.saveRule({ ...rule, ledgerId: rule.then.fundId ? (session?.ledger?.id ?? null) : null });
        setSavedRules((rs) => [saved, ...rs]);
        toast.success("הכלל נשמר");
        await flushAll();
        await runAnalyze({ keepView: true });
      } else {
        const next = [...fileRules, rule];
        setFileRules(next);
        await flushAll();
        await runAnalyze({ keepView: true, rules: next });
      }
    } catch (e) {
      toast.error(errorText(e, "החלת הכלל נכשלה"));
    }
  };
  const removeRule = async (rule: SavedRuleDto | AppliedRuleDto) => {
    try {
      if ("saved" in rule && !rule.saved) {
        const next = fileRules.filter((x) => !(x.when.field === rule.when.field && x.when.value === rule.when.value));
        setFileRules(next);
        if (step === "work") await runAnalyze({ keepView: true, rules: next });
        return;
      }
      if (!rule.id) return;
      await aiDataEntryApi.deleteRule(rule.id);
      setSavedRules((rs) => rs.filter((x) => x.id !== rule.id));
      if (step === "work") await runAnalyze({ keepView: true });
    } catch (e) {
      toast.error(errorText(e, "מחיקת הכלל נכשלה"));
    }
  };

  /* ── setup: sheet, ledger, defaults ── */
  const chooseSheet = async (sheet: number) => {
    if (!sid || sheetBusy) return;
    setSheetBusy(true);
    try {
      toSetup(await aiDataEntryApi.selectSheet(sid, sheet));
    } catch (e) {
      toast.error(errorText(e, "קריאת הגיליון נכשלה"));
    } finally {
      setSheetBusy(false);
    }
  };
  const setLedger = async (ledgerId: string | null) => {
    if (!sid) return;
    try {
      if (step === "work") {
        await flushAll();
        await runAnalyze({ keepView: true, ledgerId });
        return;
      }
      setSession(await aiDataEntryApi.setOptions(sid, { ledgerId }));
    } catch (e) {
      toast.error(errorText(e, "בחירת הקופה נכשלה"));
    }
  };
  const setDefaults = async (d: Partial<DataEntrySessionDto["defaults"]>) => {
    if (!sid) return;
    try {
      setSession(await aiDataEntryApi.setOptions(sid, { defaults: d }));
      if (step === "work") toast("ברירות המחדל יחולו בסריקה הבאה («בדיקה מחדש»)");
    } catch (e) {
      toast.error(errorText(e, "השמירה נכשלה"));
    }
  };

  /* ── counterparty ── */
  const similarOf = useCallback((row: LocalRow | undefined) => {
    if (!row) return [] as number[];
    const key = normName(row.newEntity?.name);
    const src = normName(row.source);
    return rowsRef.current
      .filter(
        (r) =>
          r.index !== row.index &&
          !isFinished(r) &&
          !r.counterparty?.confirmed &&
          wantedCounterpartyType(r.kind) === wantedCounterpartyType(row.kind) &&
          ((key && normName(r.newEntity?.name) === key) || (src && normName(r.source) === src)),
      )
      .map((r) => r.index);
  }, []);

  const pickCounterparty = useCallback(
    (index: number, cp: CounterpartyDto | null) => {
      const row = rowsRef.current[index];
      if (!row) return;
      onChange(index, { counterparty: cp, newEntity: cp ? null : row.newEntity }, "counterparty");
      if (cp && !row.existing) void requestSuggest(index, row.kind, cp);
    },
    [onChange, requestSuggest],
  );

  const applySimilar = useCallback(
    (index: number) => {
      const row = rowsRef.current[index];
      const cp = row?.counterparty;
      if (!row || !cp) return;
      const targets = similarOf(row);
      targets.forEach((i) =>
        patchRow(i, (r) => {
          const next: LocalRow = { ...r, counterparty: { ...cp, matchType: cp.matchType === "created" ? "created" : "manual", confirmed: true, ambiguous: false }, newEntity: null };
          next.status = statusOf(next);
          return next;
        }),
      );
      targets.slice(0, 40).forEach((i) => void requestSuggest(i, rowsRef.current[i]!.kind, cp));
      toast.success(`${targets.length} שורות שויכו ל${cp.label}`);
    },
    [similarOf, patchRow, requestSuggest, statusOf, toast],
  );

  const openCreate = (index: number) => {
    const row = rowsRef.current[index];
    const type = row ? wantedCounterpartyType(row.kind) : null;
    if (!row || !type) return;
    if (type === "supplier" && !hasLedger) {
      toast.error("בחרו קופה לפני הוספת ספק");
      return;
    }
    setCreating({ type, index, name: row.newEntity?.name ?? "", phone: row.newEntity?.phone ?? "", email: row.newEntity?.email ?? "", idNumber: row.newEntity?.idNumber ?? "", busy: false });
  };

  const saveCreate = async () => {
    if (!creating || !sid || !creating.name.trim()) return;
    setCreating({ ...creating, busy: true });
    try {
      const cp = await aiDataEntryApi.createCounterparty(sid, { type: creating.type, name: creating.name.trim(), phone: creating.phone || null, email: creating.email || null, idNumber: creating.idNumber || null });
      const index = creating.index;
      setCreating(null);
      const before = rowsRef.current[index];
      pickCounterparty(index, cp);
      committedAny.current = true;
      // same name in other rows → the same new customer / supplier
      const targets = similarOf(before ? { ...before, counterparty: null } : undefined);
      if (targets.length) {
        targets.forEach((i) =>
          patchRow(i, (r) => {
            const next: LocalRow = { ...r, counterparty: cp, newEntity: null };
            next.status = statusOf(next);
            return next;
          }),
        );
        targets.slice(0, 40).forEach((i) => void requestSuggest(i, rowsRef.current[i]!.kind, cp));
        toast.success(`${cp.label} שויך גם ל-${targets.length} שורות נוספות`);
      } else toast.success(`${cp.label} נוסף`);
    } catch (e) {
      toast.error(errorText(e, "ההוספה נכשלה"));
      setCreating((c) => (c ? { ...c, busy: false } : c));
    }
  };

  /* ── approve / undo ── */
  const goSummary = () => setTab("summary");

  const approve = useCallback(
    (index: number) => {
      const row = rowsRef.current[index];
      if (!row) return;
      if (isFinished(row) || rowState(row) === "saving") {
        if (!goNext(index)) setTab("summary");
        return;
      }
      const problems = rowProblems(row, { hasLedger: Boolean(sessionRef.current?.ledger) });
      if (problems.length) {
        setFlash(true);
        window.setTimeout(() => setFlash(false), 500);
        const f = problems[0]!.field;
        if (f === "amount") amountRef.current?.focus();
        else if (f === "date") dateRef.current?.focus();
        else if (f === "payment") paymentRef.current?.querySelector<HTMLElement>("button,select,input,[tabindex]")?.focus();
        return;
      }
      if (row.kind === "skip" || (!row.existing && row.duplicateAction === "skip")) {
        patchRow(index, { approved: true, commit: { state: "done", result: { index, ok: true, skipped: true, local: true } } });
      } else if (row.existing && row.existingAction !== "create") {
        patchRow(index, { approved: true, commit: { state: "done", result: { index, ok: true, kept: true, local: true } } });
      } else {
        patchRow(index, { approved: true, commit: { state: "queued" } });
        queue.current.add(index);
        schedule();
      }
      // rowsRef is stale until re-render — move past this row explicitly
      const rs = rowsRef.current;
      const f = filter.startsWith("flag:") ? filter : "todo";
      let target = -1;
      for (let i = index + 1; i < rs.length && target < 0; i += 1) if (!isFinished(rs[i]) && matchesFilter(rs[i], f)) target = i;
      for (let i = 0; i < index && target < 0; i += 1) if (!isFinished(rs[i]) && matchesFilter(rs[i], f)) target = i;
      if (target >= 0) setCur(target);
      else window.setTimeout(() => setTab("summary"), 300);
    },
    [goNext, patchRow, schedule, matchesFilter, filter],
  );

  const approveMany = useCallback(
    (indexes: number[]) => {
      const ledger = Boolean(sessionRef.current?.ledger);
      indexes.forEach((i) => {
        const r = rowsRef.current[i];
        if (!r || isFinished(r) || rowState(r) === "saving" || rowProblems(r, { hasLedger: ledger }).length) return;
        if (r.kind === "skip" || (!r.existing && r.duplicateAction === "skip")) {
          patchRow(i, { approved: true, commit: { state: "done", result: { index: i, ok: true, skipped: true, local: true } } });
        } else if (r.existing && r.existingAction !== "create") {
          patchRow(i, { approved: true, commit: { state: "done", result: { index: i, ok: true, kept: true, local: true } } });
        } else {
          patchRow(i, { approved: true, commit: { state: "queued" } });
          queue.current.add(i);
        }
      });
      schedule();
    },
    [patchRow, schedule],
  );

  const undo = useCallback(
    async (index: number) => {
      const row = rowsRef.current[index];
      const s = sessionRef.current;
      if (!row || !s) return;
      if (row.commit.state === "done" && (row.commit.result.local || row.commit.result.skipped || row.commit.result.kept)) {
        patchRow(index, { approved: false, commit: { state: "idle" } });
        return;
      }
      try {
        await aiDataEntryApi.undo(s.id, index);
        patchRow(index, { approved: false, commit: { state: "idle" } });
        committedAny.current = true;
        toast.success("הרישום בוטל — אפשר לערוך ולאשר שוב");
      } catch (e) {
        toast.error(errorText(e, "ביטול הרישום נכשל"));
      }
    },
    [patchRow, toast],
  );

  /* ── conversation with the agent about the file ── */
  const askAbout = (r: LocalRow) => {
    setChatDraft(`שורה ${r.line}: `);
    setTab("chat");
  };
  const readyIdx = useMemo(() => rows.filter((r) => rowState(r) === "ready" && !rowProblems(r, { hasLedger }).length).map((r) => r.index), [rows, hasLedger]);
  const toRows = (f: string) => {
    setFilter(f);
    if (step === "done") setStep("work");
    setTab("rows");
  };
  const showFlag = (flag: string | null) => {
    if (!flag) {
      toRows("todo");
      return;
    }
    const first = rowsRef.current.find((r) => r.flags.includes(flag) && !isFinished(r));
    if (first) setCur(first.index);
    toRows(`flag:${flag}`);
  };

  const sendChat = async (text: string) => {
    if (!sid) return;
    setChat((m) => [...m, { id: chatId(), role: "user", text }]);
    setChatBusy(true);
    try {
      // the agent sees the rows as they are on screen now (with the user's edits)
      const d = await aiDataEntryApi.chat(sid, { message: text, rowsState: rowsStateOf(rowsRef.current) });
      applyPatches(d.patches);
      const buttons: AgentChatAction[] = [];
      for (const a of d.actions) {
        if (a.type === "columns") {
          const nextCols = columns.map((c) => {
            const ch = a.columns.find((x) => x.header === c.header);
            return ch ? { ...c, role: ch.role, confidence: 1, source: "user" as const } : c;
          });
          setColumns(nextCols);
          await flushAll();
          await runAnalyze({ keepView: true, cols: nextCols });
        } else if (a.type === "rule_saved") {
          setSavedRules((rs) => [{ id: a.rule.id, label: a.rule.label, ledgerId: null, rule: null, lastUsedAt: null }, ...rs.filter((x) => x.id !== a.rule.id)]);
        } else {
          const approveType = a.type === "approve_rows" || a.type === "approve_ready";
          buttons.push({ ...a, primary: approveType, icon: approveType ? "checkAll" : a.type === "open_tab" ? "right" : "eye" });
        }
      }
      setChat((m) => [...m, { id: chatId(), role: "agent", text: d.reply, steps: d.steps, actions: buttons }]);
      if (d.patches.length) toast.success(`הסוכן עדכן ${d.patches.length} שורות — עדיין לא נרשמו, מחכות לאישור`);
    } catch (e) {
      setChat((m) => [...m, { id: chatId(), role: "agent", error: true, text: errorText(e, "הסוכן לא הצליח לענות כרגע") }]);
    } finally {
      setChatBusy(false);
    }
  };
  const onChatAction = (a: AgentChatAction, msg: AgentChatMessage) => {
    const idx = Array.isArray(a.indexes) ? (a.indexes as number[]) : [];
    if (a.type === "approve_ready") approveMany(readyIdx);
    else if (a.type === "approve_rows") approveMany(idx);
    else if (a.type === "open_row" && idx.length) {
      setFilter("todo");
      setCur(idx[0]!);
      setTab("rows");
      return;
    } else if (a.type === "show_rows") {
      if (typeof a.flag === "string" && a.flag) showFlag(a.flag);
      else if (idx.length) {
        setCur(idx[0]!);
        toRows(`idx:${idx.join(",")}`);
      }
      return;
    } else if (a.type === "open_tab" && typeof a.tab === "string") {
      setTab(a.tab);
      return;
    }
    setChat((ms) => ms.map((m) => (m.id === msg.id ? { ...m, actions: (m.actions ?? []).map((x) => (x === a ? { ...x, done: true, doneLabel: "נשלח לרישום" } : x)) } : m)));
  };
  const clearChat = async () => {
    try {
      if (sid) await aiDataEntryApi.clearChat(sid);
    } catch {
      /* local only */
    }
    setChat([]);
  };

  /* ── keyboard ── */
  const row = rows[cur];
  const startScanRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    if (!open || step !== "setup") return undefined;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key === "Enter" && !e.shiftKey && !e.defaultPrevented && t?.tagName !== "BUTTON" && t?.tagName !== "SELECT" && !t?.closest?.(".ck-combo")) {
        e.preventDefault();
        startScanRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  useEffect(() => {
    if (!open || step !== "work" || tab !== "rows" || creating || !row) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
        if (t?.tagName === "BUTTON" || t?.tagName === "TEXTAREA") return;
        // capture phase runs before the ComboBox — an open result list owns Enter
        if (t?.closest?.(".ck-combo") && document.querySelector(".ck-combo__list")) return;
        e.preventDefault();
        if (e.shiftKey) goBack(cur);
        else approve(cur);
        return;
      }
      if (e.altKey && /^Digit[0-9]$/.test(e.code)) {
        // the shell binds Alt+1..9 to its quick actions — inside the agent they change the row kind
        e.preventDefault();
        e.stopPropagation();
        const k = KIND_ORDER.find((x) => KIND_META[x].key === e.code.slice(5));
        if (k && !isFinished(row) && !row.existing && (lookups.kinds.includes(k) || k === "skip")) onChange(cur, { kind: k }, "kind");
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        if (!goNext(cur, { skipFinished: false })) setCur(Math.min(cur + 1, rows.length - 1));
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        goBack(cur);
        return;
      }
      if (!isTypingTarget(e.target) && /^[1-4]$/.test(e.key) && !e.altKey && !e.ctrlKey) {
        const want = wantedCounterpartyType(row.kind);
        const cands = row.candidates.filter((c) => c.type === want);
        const c = cands[Number(e.key) - 1];
        if (c && !isFinished(row)) {
          e.preventDefault();
          pickCounterparty(cur, { ...c, confirmed: true, ambiguous: false });
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, step, tab, creating, row, cur, rows.length, approve, goBack, goNext, onChange, pickCounterparty, lookups.kinds]);

  // outside the card deck, still keep the shell's Alt+digit quick actions from firing behind the sheet
  useEffect(() => {
    if (!open) return undefined;
    const block = (e: KeyboardEvent) => {
      if (e.altKey && /^Digit[0-9]$/.test(e.code) && !(step === "work" && tab === "rows")) e.stopPropagation();
    };
    window.addEventListener("keydown", block, true);
    return () => window.removeEventListener("keydown", block, true);
  }, [open, step, tab]);

  /* ── focus: the sheet focuses its close button on open — keep Enter away from it ── */
  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => {
      const root = document.querySelector(".ck-sheet.is-open");
      if (!root) return;
      if (step === "upload") root.querySelector<HTMLElement>(".ck-drop")?.focus();
      else if (step === "setup") root.querySelector<HTMLElement>(".ck-card__actions .ck-btn--primary")?.focus();
      else if (step === "work" && tab === "rows") root.querySelector<HTMLElement>(".de-card")?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(t);
  }, [open, step, tab, cur]);

  /* ── close ── */
  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => window.clearTimeout(flushTimer.current), []);

  const pendingCount = rows.filter((r) => !isFinished(r) && r.kind !== "skip").length;
  const close = async () => {
    if (queue.current.size || flushing.current) await flushAll();
    if (step === "work" && pendingCount > 0) {
      const ok = await ckConfirm({ title: "לצאת מההזנה?", message: `${pendingCount} שורות עדיין לא אושרו. מה שכבר אושר נשמר במערכת, והקובץ נשאר ברשימת הקבצים.`, confirmLabel: "יציאה" });
      if (!ok) return;
    }
    if (committedAny.current) {
      notifyDataChanged("payment");
      notifyDataChanged("income");
      notifyDataChanged("expense");
    }
    reset();
    onClose?.();
  };

  /* ── derived ── */
  const counts: Counts = useMemo(() => {
    const c: Counts = { total: rows.length, done: 0, skipped: 0, review: 0, ready: 0, error: 0, saving: 0, kept: 0 };
    rows.forEach((r) => {
      const s = rowState(r);
      if (s === "done") c.done += 1;
      else if (s === "kept") {
        c.kept += 1;
        c.skipped += 1;
      } else if (s === "skipped") c.skipped += 1;
      else if (s === "review") c.review += 1;
      else if (s === "ready") c.ready += 1;
      else if (s === "error") c.error += 1;
      else if (s === "saving") c.saving += 1;
    });
    return c;
  }, [rows]);
  const progress = rows.length ? ((counts.done + counts.skipped) / rows.length) * 100 : 0;
  const position = visible.indexOf(cur) + 1 || cur + 1;
  const isWork = step === "work" || step === "done";
  const hasAmountCol = columns.some((c) => c.role === "amount" || c.role === "credit" || c.role === "debit");
  const startScan = () => {
    // a list of customers / suppliers: the entity flow (its own card)
    if (isEntityImport(importType)) {
      if (!columns.some((c) => c.role === "name")) {
        toast.error("לא זוהתה עמודת שם — סמנו אותה בלשונית «עמודות»");
        setTab("columns");
        return;
      }
      if (importType === "suppliers" && !session?.ledger) {
        toast.error("בחרו קופה — הספקים שייכים לקופה");
        return;
      }
      setSession((cur) => (cur ? { ...cur, columns } : cur));
      setEntityTarget(importType);
      setStep("entity");
      return;
    }
    if (!hasAmountCol) {
      toast.error("לא זוהתה עמודת סכום — סמנו אותה בלשונית «עמודות»");
      setTab("columns");
      return;
    }
    void runAnalyze();
  };
  startScanRef.current = startScan;
  const otherFile = () => reset();
  const downloadLeft = () => {
    const left = rows.filter((r) => !isFinished(r) && r.kind !== "skip");
    const headers = session?.headers ?? [];
    const lines = [
      ["שורה", ...headers, "סיבה"].map(csvCell).join(","),
      ...left.map((r) =>
        [r.line, ...headers.map((h) => r.raw.find((c) => c.header === h)?.value ?? ""), r.commit.state === "error" ? r.commit.error : rowProblems(r, { hasLedger }).map((p) => p.text).join(", ") || "לא אושרה"]
          .map(csvCell)
          .join(","),
      ),
    ];
    const blob = new Blob([`${String.fromCharCode(0xfeff)}${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "הזנה-חכמה-שורות-שלא-נרשמו.csv";
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const warnings = session?.insights?.warnings ?? [];
  const filterLabel =
    filter === "todo"
      ? null
      : filter === "review"
        ? "דורשות בדיקה"
        : filter === "done"
          ? "טופלו"
          : filter === "all"
            ? "כל השורות"
            : filter.startsWith("idx:")
              ? "השורות שהסוכן הצביע עליהן"
              : (warnings.find((w) => w.flag === filter.slice(5))?.text ?? "").replace(/^\d+\s/, "").split(" — ")[0]!.slice(0, 60) || "סינון";
  const factOf = (k: string) => session?.insights?.facts.find((f) => f.key === k);
  const todoCount = counts.review + counts.ready + counts.error;
  const existingCount = rows.filter((r) => r.existing).length;

  /* header */
  const title = session ? session.fileName : "הזנה חכמה";
  const subtitle = !session ? (
    <span>סוכן AI · מקובץ לרשומות מאומתות — בלי להקליד, ובלי ייבוא עיוור</span>
  ) : (
    <span>
      {session.totalRows.toLocaleString("he-IL")} שורות · גיליון «{session.sheetName}»{session.ledger ? ` · קופה: ${session.ledger.name}` : ""}
    </span>
  );
  const headTags = [
    session?.insights?.profile ? { label: session.insights.profile.label, tone: "primary" } : session?.isBank ? { label: "דף בנק", tone: "primary" } : null,
    session ? { label: session.ai.note ? "AI כבוי" : "AI פעיל", tone: session.ai.note ? "neutral" : "violet" } : null,
    isWork ? { label: `${Math.round(progress)}% טופל`, tone: progress >= 100 ? "success" : "neutral" } : null,
  ].filter(Boolean);

  const auditAction = { key: "audit", label: "בדיקת נתונים קיימים", icon: "check", onClick: () => void close().then(() => navigate("/ai/data-audit")) };
  const recurringAction = { key: "recurring", label: session?.recurring ? `העלאה קבועה · ${session.recurring.name}` : "העלאה קבועה…", icon: "repeat", onClick: () => setRecurringOpen(true) };
  const actions =
    step === "setup"
      ? [
          { key: "scan", label: isEntityImport(importType) ? "בדיקת הרשימה" : "התחלת סריקה", icon: "sparkle", primary: true, onClick: startScan },
          { key: "other", label: "קובץ אחר", icon: "upload", onClick: otherFile },
        ]
      : isWork
        ? [
            ...(readyIdx.length ? [{ key: "approve", label: `אישור כל המוכנות (${readyIdx.length})`, icon: "checkAll", primary: true, onClick: () => approveMany(readyIdx) }] : []),
            ...(tab === "chat" ? [] : [{ key: "ask", label: "לשאול את הסוכן", icon: "sparkle", onClick: () => setTab("chat") }]),
            ...(step === "done" || tab === "summary"
              ? []
              : [
                  {
                    key: "finish",
                    label: "סיום וסיכום",
                    icon: "check",
                    onClick: async () => {
                      await flushAll();
                      goSummary();
                    },
                  },
                ]),
          ]
        : step === "upload"
          ? [auditAction]
          : [];
  const more = isWork
    ? [
        {
          key: "recheck",
          label: "בדיקה מחדש מול המערכת",
          icon: "refresh",
          onClick: async () => {
            await flushAll();
            await runAnalyze({ keepView: true });
          },
        },
        { key: "download", label: "הורדת השורות שלא נרשמו", icon: "download", onClick: downloadLeft },
        recurringAction,
        { divider: true },
        {
          key: "other",
          label: "קובץ אחר",
          icon: "upload",
          onClick: async () => {
            await flushAll();
            if (pendingCount) {
              const ok = await ckConfirm({ title: "לעבור לקובץ אחר?", message: `${pendingCount} שורות עדיין לא אושרו. מה שכבר אושר נשמר במערכת.`, confirmLabel: "קובץ אחר" });
              if (!ok) return;
            }
            otherFile();
          },
        },
      ]
    : step === "setup"
      ? [recurringAction]
      : [];

  const unusedCols = columns.filter((c) => c.role === "ignore").length;
  const weakCols = columns.filter((c) => c.role !== "ignore" && c.source === "heuristic" && c.confidence < 0.6).length;
  const kpis =
    step === "setup" || step === "scan"
      ? [
          { key: "rows", label: "שורות בקובץ", value: session?.totalRows.toLocaleString("he-IL") },
          { key: "cols", label: "עמודות שזוהו", value: `${columns.length - unusedCols} / ${columns.length}`, onClick: step === "setup" ? () => setTab("columns") : undefined },
          { key: "weak", label: "לא בטוח", value: weakCols || "—", attn: weakCols > 0, sub: weakCols ? "כדאי לבדוק" : "הכל ברור", onClick: step === "setup" && weakCols ? () => setTab("columns") : undefined },
          { key: "rules", label: "כללים שמורים", value: savedRules.length || "—", sub: savedRules.length ? "יוחלו אוטומטית" : null },
        ]
      : isWork
        ? [
            { key: "todo", label: "לטיפול", value: todoCount.toLocaleString("he-IL"), sub: counts.saving ? "שומר…" : `מתוך ${counts.total}`, onClick: () => toRows("todo") },
            { key: "review", label: "דורשות בדיקה", value: (counts.review + counts.error).toLocaleString("he-IL"), attn: counts.review + counts.error > 0, onClick: () => toRows("review") },
            { key: "existing", label: "כבר במערכת", value: existingCount.toLocaleString("he-IL"), onClick: existingCount ? () => showFlag("existing_any") : undefined },
            { key: "done", label: "טופלו", value: (counts.done + counts.skipped).toLocaleString("he-IL"), sub: `${Math.round(progress)}%`, onClick: () => toRows("done") },
            factOf("in") ? { key: "in", label: factOf("in")!.label, value: money(Number(factOf("in")!.value)) } : null,
            factOf("out")?.value ? { key: "out", label: factOf("out")!.label, value: money(Number(factOf("out")!.value)) } : null,
          ].filter(Boolean)
        : [];

  /* tabs */
  const chatPane = () => (
    <div className="de-main de-main--chat">
      <AgentChat
        messages={chat}
        busy={chatBusy}
        onSend={(t) => void sendChat(t)}
        onAction={onChatAction}
        draft={chatDraft}
        setDraft={setChatDraft}
        starters={FILE_STARTERS}
        hint="הנחיות, תיקונים ושאלות על הקובץ. הסוכן מעדכן את השורות על המסך — נרשם רק מה שתאשרו."
        emptyText="דברו עם הסוכן כמו עם עובד: «כל מה שכתוב בו ביט הוא תשלום מלקוח», «עמודה ג' היא הנציג», «למה שורה 12 היא הוצאה?», «טעית — זה לא אותו כהן»."
        onClear={() => void clearChat()}
        feedback={{ surface: "data_entry" }}
      />
    </div>
  );
  const note: ReactNode = actionNote ? (
    <Banner
      tone="info"
      action={
        <Button size="sm" icon="x" onClick={() => setActionNote(null)}>
          סגירה
        </Button>
      }
    >
      {actionNote}
    </Banner>
  ) : null;
  const deckPane = () => (
    <div className="de-main">
      {note}
      {!hasLedger && rows.some((r) => !isFinished(r) && (r.kind === "income" || r.kind === "expense")) ? (
        <Banner tone="warning">כדי לרשום הכנסות והוצאות צריך לבחור קופה — בצד, «ברירות מחדל לכל הקובץ».</Banner>
      ) : null}
      {filterLabel ? (
        <div className="de-filterbar">
          <span className="de-muted">מוצגות:</span>
          <b>{filterLabel}</b>
          <span className="de-muted">· {visible.length} שורות</span>
          <span className="ck-grow" />
          <button type="button" className="ck-link" onClick={() => setFilter("todo")}>
            כל השורות לטיפול
          </button>
        </div>
      ) : null}
      {row && sid ? (
        <DataEntryCard
          key={row.index}
          row={row}
          position={position}
          total={visible.length || rows.length}
          sessionId={sid}
          lookups={lookups}
          hasLedger={hasLedger}
          flash={flash}
          fieldRefs={fieldRefs}
          onChange={(patch, field) => onChange(row.index, patch, field)}
          onPick={(cp) => pickCounterparty(row.index, cp)}
          onCreate={() => openCreate(row.index)}
          similarCount={row.counterparty ? similarOf(row).length : 0}
          onApplySimilar={() => applySimilar(row.index)}
          onApprove={() => approve(row.index)}
          onBack={() => goBack(row.index)}
          onUndo={() => void undo(row.index)}
          learn={learn && learn.index === row.index ? learn : null}
          onLearnApply={(g, save) => void applyLearn(g, save)}
          onLearnDismiss={() => setLearn(null)}
          onAsk={() => askAbout(row)}
        />
      ) : (
        <EmptyFilter onClear={() => setFilter("todo")} />
      )}
    </div>
  );
  const tablePane = () => (
    <div className="de-main">
      <div className="de-filterbar">
        <Segmented
          ariaLabel="סינון"
          value={["todo", "review", "done", "all"].includes(filter) ? filter : ""}
          onChange={setFilter}
          items={[
            { value: "todo", label: "לטיפול", count: todoCount },
            { value: "review", label: "דורשות בדיקה", count: counts.review + counts.error },
            { value: "done", label: "טופלו", count: counts.done + counts.skipped },
            { value: "all", label: "הכל", count: counts.total },
          ]}
        />
        {filter.startsWith("flag:") || filter.startsWith("idx:") ? (
          <>
            <span className="de-muted">· {filterLabel}</span>
            <button type="button" className="ck-link" onClick={() => setFilter("todo")}>
              ניקוי
            </button>
          </>
        ) : null}
      </div>
      <DataEntryTable
        rows={rows}
        visible={visible}
        kinds={lookups.kinds}
        hasLedger={hasLedger}
        onOpen={(i) => {
          setCur(i);
          setTab("rows");
        }}
        onKind={(i, k) => onChange(i, { kind: k }, "kind")}
        onApproveMany={approveMany}
      />
    </div>
  );
  const analysisPane = () => (
    <div className="de-main">
      {note}
      {session?.ai.error ? <Banner tone="warning">ה-AI לא השלים את כל השורות — שאר השורות סווגו לפי כללים.</Banner> : null}
      {session?.ai.capped ? <Banner tone="info">ה-AI עבר על 600 השורות הראשונות; השאר סווגו לפי כללים והיסטוריה.</Banner> : null}
      {session?.insights && sid ? (
        <DataEntryInsights
          sessionId={sid}
          insights={session.insights}
          activeFlag={filter.startsWith("flag:") ? filter.slice(5) : null}
          onFlag={showFlag}
          onRecheck={() => void flushAll().then(() => runAnalyze({ keepView: true }))}
          rules={session.rules}
          suggestedRules={session.suggestedRules}
          kinds={lookups.kinds}
          onApplyRule={(r, o) => void applyRule(r, o)}
          onRemoveRule={(r) => void removeRule(r)}
        />
      ) : null}
    </div>
  );

  const tabs =
    step === "upload"
      ? [{ key: "upload", label: "העלאת קובץ", render: () => <UploadTab busy={busy} onFile={(f) => void onFile(f)} error={error} onAudit={auditAction.onClick} /> }]
      : step === "scan"
        ? [{ key: "scan", label: "סריקה", render: () => <ScanStep /> }]
        : step === "setup" && session
          ? [
              {
                key: "type",
                label: "מה בקובץ",
                render: () => (
                  <div className="de-main">
                    {error ? <Banner tone="danger">{error}</Banner> : null}
                    <TypeTab upload={session} importType={importType} setImportType={setImportType} columns={columns} />
                    {importType === "bank" ? (
                      <DataEntryBankLoad
                        session={session}
                        columns={columns}
                        choice={bankChoice}
                        onChoice={setBankChoice}
                        onLoaded={() => {
                          aiDataEntryApi
                            .get(session.id)
                            .then((s) => setSession(s))
                            .catch(() => undefined);
                          toast.success("דף הבנק נטען לקופה — אפשר להתחיל סריקה");
                        }}
                      />
                    ) : null}
                  </div>
                ),
              },
              {
                key: "columns",
                label: "עמודות",
                count: columns.length,
                render: () => (
                  <div className="de-main">
                    <ColumnsTab upload={session} columns={columns} setColumns={setColumns} />
                  </div>
                ),
              },
            ]
          : [
              { key: "rows", label: "שורה-שורה", count: todoCount, render: deckPane },
              { key: "table", label: "כל השורות", count: counts.total, render: tablePane },
              { key: "chat", label: "שיחה עם הסוכן", count: chat.filter((m) => m.role === "agent").length || undefined, render: chatPane },
              { key: "analysis", label: "ניתוח הקובץ", count: warnings.filter((w) => w.level === "danger" || w.level === "warning").length || undefined, render: analysisPane },
              {
                key: "summary",
                label: "סיכום",
                render: () => (
                  <div className="de-main">
                    <SummaryTab rows={rows} counts={counts} pendingCount={pendingCount} groups={session?.groups ?? []} onDownload={downloadLeft} onBackToRows={() => toRows("todo")} />
                  </div>
                ),
              },
            ];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0]!.key;

  /* side column */
  const defaults = session ? (
    <DefaultsSection
      upload={session}
      onLedger={(id) => void setLedger(id)}
      onDefaults={(d) => void setDefaults(d)}
      ledgerLocked={rows.some((r) => r.commit.state === "done" && !r.commit.result.local && (r.kind === "income" || r.kind === "expense"))}
    />
  ) : null;
  const side =
    step === "upload" ? (
      <>
        <HowItWorks />
        <RecurringSection items={recurring} onRemove={(r) => setRecurring((rs) => rs.filter((x) => x.id !== r.id))} />
        <SavedRulesSection rules={savedRules} onRemove={(r) => void removeRule(r)} defaultOpen={Boolean(savedRules.length)} />
      </>
    ) : step === "setup" && session ? (
      <>
        <FileSection upload={session} onSheet={(s) => void chooseSheet(s)} sheetBusy={sheetBusy} />
        {defaults}
        <SavedRulesSection rules={savedRules} onRemove={(r) => void removeRule(r)} />
      </>
    ) : step === "scan" && session ? (
      <FileSection upload={session} />
    ) : activeTab === "chat" ? (
      <>
        <ProgressSection counts={counts} progress={progress} filterLabel={filterLabel} onClearFilter={() => setFilter("todo")} />
        <ChatTips />
      </>
    ) : activeTab === "rows" && row && session ? (
      <>
        <RowFileSection row={row} roles={session.roles} />
        <RowUnderstandingSection row={row} />
        <NearbySection rows={rows} cur={cur} onPick={setCur} />
      </>
    ) : session ? (
      <>
        <ProgressSection counts={counts} progress={progress} filterLabel={filterLabel} onClearFilter={() => setFilter("todo")} />
        {defaults}
        <FileSection upload={session} />
      </>
    ) : null;

  const recurringDialog = session ? (
    <RecurringUploadDialog
      open={recurringOpen}
      onClose={() => setRecurringOpen(false)}
      session={session}
      importType={importType}
      columns={columns}
      onSaved={(r) => {
        setRecurring((rs) => [r, ...rs.filter((x) => x.id !== r.id)]);
        setSession((cur) => (cur ? { ...cur, recurring: { id: r.id, name: r.name, frequencyLabel: r.frequencyLabel, nextDueDate: r.nextDueDate, applied: false } } : cur));
      }}
    />
  ) : null;

  // a list of customers / suppliers: its own card (Turbo's DataEntryEntity)
  if (step === "entity" && session && entityTarget) {
    return (
      <>
        <DataEntryEntity
          open={open}
          session={session}
          target={entityTarget}
          onClose={() => {
            reset();
            onClose?.();
          }}
          onBackToSetup={() => {
            setEntityTarget(null);
            setStep("setup");
            setTab("type");
          }}
          onOtherFile={reset}
        />
        {recurringDialog}
      </>
    );
  }

  return (
    <>
      {recurringDialog}
      <EntityCard
        open={open}
        onClose={() => void close()}
        width={1320}
        title={title}
        subtitle={subtitle}
        avatarText="✦"
        tags={headTags}
        actions={actions}
        more={more}
        kpis={kpis}
        tabs={tabs}
        tab={activeTab}
        loading={busy && !session && Boolean(sessionId)}
        onTabChange={(k: string) => {
          if (step === "done" && k !== "summary") setStep("work");
          setTab(k);
        }}
        onPrev={isWork && activeTab === "rows" && row ? () => goBack(cur) : undefined}
        onNext={
          isWork && activeTab === "rows" && row
            ? () => {
                if (!goNext(cur, { skipFinished: false })) setCur(Math.min(cur + 1, rows.length - 1));
              }
            : undefined
        }
        detailsTop={<div className="de-side-col">{side}</div>}
      />
      <Dialog
        open={Boolean(creating)}
        onClose={() => setCreating(null)}
        title={creating?.type === "supplier" ? "ספק חדש" : "לקוח חדש"}
        icon="plus"
        size="md"
        footer={
          <>
            <Button onClick={() => setCreating(null)}>ביטול</Button>
            <Button variant="primary" icon="check" disabled={!creating?.name.trim() || creating?.busy} onClick={() => void saveCreate()}>
              {creating?.busy ? "מוסיף…" : "הוספה ושיוך"}
            </Button>
          </>
        }
      >
        {creating ? (
          <div className="de-create">
            <Field label="שם" full>
              <TextInput autoFocus value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
            </Field>
            <Field label="טלפון">
              <TextInput ltr value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
            </Field>
            <Field label="מייל">
              <TextInput ltr value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
            </Field>
            {creating.type === "customer" ? (
              <Field label="ת.ז / ח.פ">
                <TextInput ltr value={creating.idNumber} onChange={(e) => setCreating({ ...creating, idNumber: e.target.value })} />
              </Field>
            ) : null}
            <span className="de-muted">
              {creating.type === "supplier" ? `הספק יתווסף לקופה ${session?.ledger?.name ?? ""}.` : "הלקוח נוצר דרך מסך הלקוחות הרגיל (כולל בדיקת כפילות בשם)."} שורות נוספות עם אותו שם ישויכו אליו.
            </span>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
